import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Mail, Plus, Trash2, Send, Clock, Calendar } from "lucide-react";

const DAYS = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const todayStr = () => new Date().toISOString().split("T")[0];
const weekAgoStr = () => {
  const d = new Date(); d.setDate(d.getDate() - 7);
  return d.toISOString().split("T")[0];
};

export const EmailReportTab = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [config, setConfig]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [sending, setSending]   = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [dateFrom, setDateFrom] = useState(weekAgoStr());
  const [dateTo, setDateTo]     = useState(todayStr());

  useEffect(() => {
    axios.get(`${API}/noc/email-config`, { headers })
      .then(r => setConfig(r.data))
      .catch(() => toast.error("Failed to load email config"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/noc/email-config`, config, { headers });
      toast.success("Email config saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    if (!dateFrom || !dateTo) { toast.error("Select a date range first"); return; }
    if (dateFrom > dateTo) { toast.error("Start date must be before end date"); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/noc/email-config/send-now`,
        { date_from: dateFrom, date_to: dateTo },
        { headers }
      );
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const addRecipient = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) return;
    if (config.recipients.includes(email)) { toast.error("Already added"); return; }
    setConfig(c => ({ ...c, recipients: [...c.recipients, email] }));
    setNewEmail("");
  };

  const removeRecipient = (email) =>
    setConfig(c => ({ ...c, recipients: c.recipients.filter(r => r !== email) }));

  const fmt12 = (h) => {
    const ampm = h >= 12 ? "PM" : "AM";
    const h12  = h % 12 || 12;
    return `${h12}:00 ${ampm}`;
  };

  if (!config) return (
    <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
      Loading…
    </div>
  );

  return (
    <div className="space-y-6 max-w-2xl">

      {/* Recipients */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4 w-4" /> Recipients
          </CardTitle>
          <CardDescription>Who receives the weekly report email.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="name@company.com"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addRecipient()}
              className="flex-1"
            />
            <Button variant="outline" onClick={addRecipient}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {config.recipients.length === 0 ? (
            <p className="text-sm text-muted-foreground">No recipients added yet.</p>
          ) : (
            <div className="space-y-1">
              {config.recipients.map(r => (
                <div key={r} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-muted/50 text-sm">
                  <span>{r}</span>
                  <button onClick={() => removeRecipient(r)} className="text-muted-foreground hover:text-red-500 transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" /> Schedule
          </CardTitle>
          <CardDescription>When the report is sent automatically each week.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-medium">Day</p>
              <Select value={config.schedule_day} onValueChange={v => setConfig(c => ({ ...c, schedule_day: v }))}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map(d => (
                    <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-medium">Time</p>
              <Select value={String(config.schedule_hour)} onValueChange={v => setConfig(c => ({ ...c, schedule_hour: parseInt(v) }))}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOURS.map(h => (
                    <SelectItem key={h} value={String(h)}>{fmt12(h)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-medium">Enabled</p>
              <Select value={config.enabled ? "true" : "false"} onValueChange={v => setConfig(c => ({ ...c, enabled: v === "true" }))}>
                <SelectTrigger className="w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Enabled</SelectItem>
                  <SelectItem value="false">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {config.enabled && (
            <p className="text-xs text-muted-foreground">
              Report will be sent every <span className="font-medium capitalize">{config.schedule_day}</span> at <span className="font-medium">{fmt12(config.schedule_hour)}</span>.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Send Report */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-4 w-4" /> Send Report
          </CardTitle>
          <CardDescription>Choose a date range and send the report to all recipients.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-medium">From</p>
              <Input
                type="date"
                value={dateFrom}
                max={dateTo}
                onChange={e => setDateFrom(e.target.value)}
                className="w-[160px]"
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-medium">To</p>
              <Input
                type="date"
                value={dateTo}
                min={dateFrom}
                max={todayStr()}
                onChange={e => setDateTo(e.target.value)}
                className="w-[160px]"
              />
            </div>
            <Button
              onClick={sendNow}
              disabled={sending || config.recipients.length === 0}
              className="flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              {sending ? "Sending…" : "Send Report"}
            </Button>
          </div>
          {config.recipients.length === 0 && (
            <p className="text-xs text-muted-foreground">Add recipients above before sending.</p>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </div>

    </div>
  );
};
